import React, { FC, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import { GisIntakeStep, STEP_ROUTE_SUFFIX } from "@app/service/intakeForm";
import { UnderstandingOption } from "@app/utils/intakeFormOptions";
import { IntakeFormStepLayout } from "../components";
import { useIntakeForm } from "../context";

const messages = defineMessages({
  title: {
    id: "intakeStepUnderstanding.title",
    defaultMessage:
      "How easy or hard has it been for you to understand the information you've received so far?",
  },
  optionEasy: {
    id: "intakeStepUnderstanding.optionEasy",
    defaultMessage: "Easy",
  },
  optionHard: {
    id: "intakeStepUnderstanding.optionHard",
    defaultMessage: "Hard",
  },
  optionVeryHard: {
    id: "intakeStepUnderstanding.optionVeryHard",
    defaultMessage: "Very Hard",
  },
  nextButton: {
    id: "intakeStepUnderstanding.nextButton",
    defaultMessage: "Next",
  },
});

const IntakeStepUnderstanding: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const {
    userInputFields,
    updateUserField,
    setCurrentStep,
    totalSteps,
    getNextStep,
    getPreviousStep,
  } = useIntakeForm();

  const currentStep = GisIntakeStep.UNDERSTANDING;
  const [selectedOption, setSelectedOption] =
    useState<UnderstandingOption | null>(
      userInputFields.easeOfUnderstandingInformation as UnderstandingOption | null
    );

  useEffect(() => {
    setCurrentStep(currentStep);
  }, [setCurrentStep]);

  const buildRoute = (step: GisIntakeStep | null) =>
    routes.scheduleAGeneticInformationSessionIntakePage(
      orderUidPublic || "",
      testUid || ""
    ) + (step ? STEP_ROUTE_SUFFIX[step] : "");

  const handleOptionChange = (value: UnderstandingOption) => {
    setSelectedOption(value);
    updateUserField("easeOfUnderstandingInformation", value);
  };

  const handleContinue = () => {
    if (!selectedOption) return;
    navigate(buildRoute(getNextStep(currentStep)));
  };

  const handleBack = () => {
    navigate(buildRoute(getPreviousStep(currentStep)));
  };

  const options: { value: UnderstandingOption; label: string }[] = [
    { value: "easy", label: intl.formatMessage(messages.optionEasy) },
    { value: "hard", label: intl.formatMessage(messages.optionHard) },
    { value: "very_hard", label: intl.formatMessage(messages.optionVeryHard) },
  ];

  return (
    <IntakeFormStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={intl.formatMessage(messages.title)}
      onContinue={handleContinue}
      onBack={handleBack}
      continueDisabled={!selectedOption}
      continueLabel={intl.formatMessage(messages.nextButton)}
    >
      <div className="intake-form-step__radio-list">
        {options.map((option) => (
          <label
            key={option.value}
            className="intake-form-step__radio-list__item"
          >
            <input
              type="radio"
              name="understanding"
              className="intake-form-step__radio-list__input"
              checked={selectedOption === option.value}
              onChange={() => handleOptionChange(option.value)}
            />
            <span className="intake-form-step__radio-list__label">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </IntakeFormStepLayout>
  );
};

export default IntakeStepUnderstanding;
